// ============================================================================
// Upgrade Wizard Types
// ============================================================================

export type UpgradeComponentType = 'Website' | 'AdditionalUsers' | 'GoogleLocalServiceAds' | 'MetaAds';

export type BillingCycleOption = 'Annual' | 'Quarterly' | null;

// ============================================================================
// API Response Types
// ============================================================================

export interface OperationResult<T> {
	success: boolean;
	value?: T;
	error?: string;
}

export interface SubscriptionComponent {
	componentId: number;
	name: string;
	handle?: string;
	pricePointId?: number;
	allocatedQuantity: number;
}

export interface SubscriptionState {
	subscriptionId: number;
	state: string;
	productId: number;
	productName: string;
	productHandle: string;
	billingCycleMonths: number;
	billingCycleLabel: string;
	currentPeriodStartedAt: string;
	currentPeriodEndsAt: string;
	daysRemainingInPeriod: number;
	existingComponents: SubscriptionComponent[];
	isLegacyProduct: boolean;
	couponCodes?: string[];
	hasCoupons?: boolean;
	// Billing Cycle Change Eligibility
	isEligibleForBillingCycleChange: boolean;
	billingCycleChangeBlockers: string[];
	nextProductHandle?: string | null;
	nextProductId?: number | null;
}

export interface UpgradeComponentRequest {
	componentId: number;
	pricePointId: number;
	quantity: number;
}

export interface UpgradePreviewRequest {
	subscriptionId: number;
	accountId: string;
	components: UpgradeComponentRequest[];
	newBillingCycle: BillingCycleOption;
	changeBillingCycleImmediately: boolean; // false = scheduled, true = immediate
}

export interface UpgradePreviewComponent {
	componentType: number;
	componentId: number;
	componentName?: string;
	quantity: number;
	unitPrice: number;
	proratedChargeInCents: number;
	proratedCharge: number;
	discountAmountInCents?: number;
	discountAmount?: number;
	netChargeInCents?: number;
	netCharge?: number;
	warning?: string;
}

export interface UpgradePreviewResponse {
	subscriptionId: number;
	currentState: SubscriptionState;
	components: UpgradePreviewComponent[];
	totalProratedChargeInCents: number;
	totalProratedCharge: number;
	totalDiscountInCents?: number;
	totalDiscount?: number;
	netTotalInCents?: number;
	netTotal?: number;
	totalTaxInCents: number;
	totalInCents: number;
	daysRemainingInPeriod: number;
	// Immediate Billing Cycle Change Fields
	isImmediateBillingCycleChange: boolean;
	serviceCreditInCents: number;
	serviceCredit: number;
	newBillingCycleChargesInCents: number;
	newBillingCycleCharges: number;
	amountDueTodayInCents: number;
	amountDueToday: number;
	remainingCreditInCents: number;
	remainingCredit: number;
	scheduledProductChange?: string | null;
}

export interface UpgradeExecuteRequest {
	subscriptionId: number;
	accountId: string;
	components: UpgradeComponentRequest[];
	newBillingCycle: BillingCycleOption;
	changeBillingCycleImmediately: boolean; // false = scheduled, true = immediate
}

export interface UpgradeExecuteComponentResult {
	componentType: number;
	componentId: number;
	componentName: string;
	allocationId: number;
	quantity: number;
	amountChargedInCents: number;
	amountCharged: number;
	success: boolean;
}

export interface UpgradeExecuteResponse {
	success: boolean;
	subscriptionId: number;
	componentResults: UpgradeExecuteComponentResult[];
	totalChargedInCents: number;
	totalCharged: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Determines which upgrade components are available based on existing subscription components.
 * Filters out components that the account already has (allocatedQuantity > 0).
 */
export function getAvailableUpgradeComponents(existingComponents: SubscriptionComponent[]): UpgradeComponentType[] {
	// Filter to only components that are actually allocated (quantity > 0)
	const allocatedComponents = existingComponents.filter(c => c.allocatedQuantity > 0);
	const allocatedNames = allocatedComponents.map(c => c.name.toLowerCase());

	const availableComponents: UpgradeComponentType[] = [];

	// Check for Website - hide if any allocated component contains "website"
	const hasWebsite = allocatedNames.some(name => name.includes('website'));
	if (!hasWebsite) {
		availableComponents.push('Website');
	}

	// Check for AdditionalUsers - hide if any allocated component contains "user"
	const hasUsers = allocatedNames.some(name => name.includes('user'));
	if (!hasUsers) {
		availableComponents.push('AdditionalUsers');
	}

	// Check for GoogleLocalServiceAds - hide if any allocated component contains "google" or "lsa" or "local service"
	const hasLSA = allocatedNames.some(
		name => name.includes('google') || name.includes('lsa') || name.includes('local service')
	);
	if (!hasLSA) {
		availableComponents.push('GoogleLocalServiceAds');
	}

	// Check for MetaAds - hide if any allocated component contains "facebook" or "meta" or "fb ads"
	const hasMetaAds = allocatedNames.some(
		name => name.includes('facebook') || name.includes('meta') || name.includes('fb ads')
	);
	if (!hasMetaAds) {
		availableComponents.push('MetaAds');
	}

	return availableComponents;
}

/**
 * Returns a user-friendly display name for a component type.
 */
export function getComponentDisplayName(componentType: UpgradeComponentType): string {
	switch (componentType) {
		case 'Website':
			return 'Website';
		case 'AdditionalUsers':
			return 'Additional Users';
		case 'GoogleLocalServiceAds':
			return 'Google LSA';
		case 'MetaAds':
			return 'Meta Ads';
		default:
			return componentType;
	}
}
